import { useTranslation } from 'react-i18next';
import { FaCopy as CopyIcon } from 'react-icons/fa';
import { Button, Input, Label, useToast } from '@knack/asterisk-react';

import { type Credentials } from '@/types/auth';
import { type LoginView } from '@/types/schema/views/LoginView';
import { useApplicationQuery } from '@/hooks/api/queries/useApplicationQuery';
import { useSignIn2faForm } from '@/hooks/useSignIn2faForm';
import { copyToClipboard } from '@/utils/copy-to-clipboard';
import { useViewContext } from '@/components/views/ViewContext';
import { useThemingContext } from '@/context/ThemingContext';
import { TwoFaCountdown } from './TwoFaCountdown';

/**
 * Renders a two-factor authentication (2FA) setup form with QR code, secret display, countdown timer, and code verification input.
 *
 * Guides the user through scanning a QR code or entering a secret into an authenticator app, displays the secret with copy-to-clipboard functionality, and provides a countdown timer with optional refresh for expiring secrets. Includes input validation for the 2FA code and localized UI elements.
 *
 * @param credentials - The user's credentials required for 2FA setup.
 * @param data - Contains the 2FA secret, QR code image URI, and optional expiration timestamp for the secret.
 * @param onRefresh - Optional callback to refresh the 2FA secret when the countdown expires.
 * @param isRefreshing - Indicates if the secret refresh is currently in progress.
 */
export function SignIn2faSetup({
  credentials,
  data,
  onRefresh,
  isRefreshing = false
}: {
  credentials: Credentials;
  data: { secret: string; dataUri: string; expires?: number };
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  const [t] = useTranslation();
  const { presentToast } = useToast();
  const { view } = useViewContext<LoginView>();
  const { data: application } = useApplicationQuery();
  const { theme } = useThemingContext();
  const logoWidth = 150 * (theme.mainNavContainer.logo.imageWidthPercentage / 100);

  const { register, handleSubmit, errors, isSubmitting, isValid, onSubmitHandler } =
    useSignIn2faForm({ credentials });

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  // This function only works in localhost or https domains due to browser security restrictions
  async function copySecretToClipboard(secret: string) {
    try {
      await copyToClipboard(secret);
      presentToast({
        title: t('components.two_factor_auth.setup.copy.success')
      });
    } catch (error) {
      presentToast({
        title: t('components.two_factor_auth.setup.copy.error')
      });
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)} className="w-full">
      {theme.mainNavContainer.logo.isVisible && (
        <div className="mb-4 flex w-full items-center justify-center gap-2 sm:mb-6 sm:justify-center">
          <img
            src={theme.mainNavContainer.logo.url}
            alt={application?.name}
            style={{ minWidth: `${logoWidth}px`, maxWidth: `${logoWidth}px` }}
          />
        </div>
      )}
      <h3
        className="mb-4 self-start text-center text-xl text-emphasis sm:mb-6 sm:self-center"
        data-testid="twofa-setup-title"
      >
        {t('components.two_factor_auth.setup.title')}
      </h3>

      <p className="mb-4 text-center text-sm" data-testid="twofa-setup-description">
        {t('components.two_factor_auth.setup.description')}
      </p>

      <ol className="mb-8" data-testid="twofa-setup-steps">
        <li className="mb-4" data-testid="twofa-setup-step-1">
          <p className="font-semibold">
            {t('components.two_factor_auth.setup.steps.step_1.title')}
            {t('components.two_factor_auth.setup.steps.step_1.description')}
          </p>
        </li>
        <li className="mb-4" data-testid="twofa-setup-step-2">
          <p className="font-semibold">
            {t('components.two_factor_auth.setup.steps.step_2.title')}
            {t('components.two_factor_auth.setup.steps.step_2.configure')}
          </p>
          <p className="mb-4">{t('components.two_factor_auth.setup.steps.step_2.description')}</p>
          <div className="mb-8 flex w-full flex-row gap-6">
            {/* QR Code on the left */}
            <div className="flex items-center justify-center">
              <img
                src={data.dataUri}
                alt={t('components.two_factor_auth.setup.qr_code')}
                className="size-48 rounded-lg border-4 border-white bg-white object-contain shadow-sm"
                data-testid="twofa-setup-qrcode"
              />
            </div>

            {/* Right side content: Secret at top, countdown at bottom */}
            <div className="flex flex-1 flex-col justify-between text-sm">
              <div>
                <p className="mb-1 font-semibold">
                  {t('components.two_factor_auth.setup.secret')}:
                </p>
                <div className="flex items-center gap-2">
                  <code className="whitespace-nowrap break-all text-sm" data-testid="two-fa-secret">
                    {data.secret}
                  </code>
                  <CopyIcon
                    className="cursor-pointer text-emphasis"
                    data-testid="twofa-setup-copy-icon"
                    onClick={() => copySecretToClipboard(data.secret)}
                  />
                </div>
              </div>

              <div className="mt-4">
                <TwoFaCountdown
                  expiresAt={data.expires}
                  onRefresh={handleRefresh}
                  isRefreshing={isRefreshing}
                />
              </div>
            </div>
          </div>
        </li>
        <li className="mb-4" data-testid="twofa-setup-step-3">
          <p className="font-semibold">
            {t('components.two_factor_auth.setup.steps.step_3.title')}
            {t('components.two_factor_auth.setup.steps.step_3.enter_code')}
          </p>
          <p>{t('components.two_factor_auth.setup.steps.step_3.description')}</p>
        </li>
      </ol>

      <Label className="mb-4" htmlFor="2fa-setup-code" intent={errors.code2fa && 'destructive'}>
        {t('components.two_factor_auth.6_digit_code')}
      </Label>
      <Input
        id="2fa-setup-code"
        maxLength={6}
        autoFocus
        disabled={isSubmitting}
        placeholder={t('components.two_factor_auth.6_digit_code_placeholder')}
        intent={errors.code2fa && 'destructive'}
        {...register('code2fa', {
          onChange: (e) => {
            const value = e.target.value.replace(/[^0-9]/g, '');
            e.target.value = value;
          }
        })}
        data-testid="twofa-setup-input-code"
        className="mt-2"
      />
      {errors.code2fa && (
        <span className="text-xs text-destructive" data-testid="twofa-setup-error">
          {t('components.two_factor_auth.setup.error')}
        </span>
      )}

      <Button
        type="submit"
        intent="primary"
        isLoading={isSubmitting}
        disabled={!isValid}
        className="mb-8 mt-8 w-full"
        data-testid="twofa-setup-submit-button"
      >
        {view.submitButtonText || t('components.two_factor_auth.submit')}
      </Button>
    </form>
  );
}
